/*
 * Glass cart drawer: close when the blurred page behind it is clicked.
 *
 * The blur is a ::before pseudo-element on the drawer's host element
 * (#cart-drawer), drawn above the page. Clicks on a pseudo-element are
 * delivered to its host, so they land on #cart-drawer itself rather than on
 * whatever page element the user was aiming at — which also meant Dwell's own
 * outside-click handling never saw them. Anything inside the dialog is the
 * drawer, so only clicks whose target is the host are treated as "outside".
 */
document.addEventListener('click', (event) => {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer || event.target !== drawer) return;
  if (!drawer.querySelector('dialog')?.open) return;
  drawer.close?.();
});

/*
 * Glass search popup: close when the blurred page around it is clicked.
 *
 * The search dialog covers the viewport as the blur layer and the panel floats
 * inside it; the component between them ignores pointer events (see
 * sss-search.css), so a click anywhere off the panel targets the dialog itself.
 */
document.addEventListener('click', (event) => {
  const modal = document.getElementById('search-modal');
  const dialog = modal?.querySelector('dialog');
  if (!dialog?.open || event.target !== dialog) return;
  modal.closeDialog?.();
});

/*
 * Choosing a suggestion closes the cart before the browser follows the link,
 * so the next page opens clean rather than with the drawer still over it.
 * The link itself is left to navigate normally.
 */
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const suggestion = event.target.closest('#cart-drawer .sss-cart-sug');
  if (!suggestion) return;
  document.getElementById('cart-drawer')?.close?.();
});

/*
 * Scroll-edge fades for the cart column. Sets --sss-fade-top / --sss-fade-bottom
 * on .sss-cart-scroll to 32px only where there is content hidden in that
 * direction. Recomputed on scroll, and whenever the drawer re-renders (adding,
 * removing or changing quantities replaces the markup) or opens.
 */
(() => {
  const FADE = '32px';

  const update = () => {
    const column = document.querySelector('#cart-drawer .sss-cart-scroll');
    if (!column) return;
    const { scrollTop, scrollHeight, clientHeight } = column;
    column.style.setProperty('--sss-fade-top', scrollTop > 2 ? FADE : '0px');
    column.style.setProperty(
      '--sss-fade-bottom',
      scrollTop + clientHeight < scrollHeight - 2 ? FADE : '0px'
    );
  };

  let queued = false;
  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      update();
    });
  };

  // Scroll events do not bubble; a capturing listener still receives them.
  document.addEventListener(
    'scroll',
    (event) => {
      if (event.target instanceof Element && event.target.matches('.sss-cart-scroll')) schedule();
    },
    { passive: true, capture: true }
  );

  document.addEventListener('theme-drawer:open', () => setTimeout(schedule, 50));
  window.addEventListener('resize', schedule, { passive: true });

  const watch = () => {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return;
    new MutationObserver(schedule).observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['open'] });
    schedule();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watch);
  } else {
    watch();
  }
})();

/*
 * The fades were not being computed when the drawer first opened — the column
 * has no size while the drawer is closed, and Dwell's open event did not
 * reliably reach the listener above. A ResizeObserver on the column fires the
 * moment it is laid out, which covers opening, re-rendering after a cart
 * change, and suggestions expanding. The column element is replaced on every
 * re-render, so the observer is re-attached whenever a new one appears.
 */
(() => {
  let observed = null;
  const recompute = () => {
    const column = document.querySelector('#cart-drawer .sss-cart-scroll');
    if (!column) return;
    const { scrollTop, scrollHeight, clientHeight } = column;
    column.style.setProperty('--sss-fade-top', scrollTop > 2 ? '32px' : '0px');
    column.style.setProperty(
      '--sss-fade-bottom',
      scrollTop + clientHeight < scrollHeight - 2 ? '32px' : '0px'
    );
  };

  const ro = new ResizeObserver(recompute);

  const attach = () => {
    const column = document.querySelector('#cart-drawer .sss-cart-scroll');
    if (column && column !== observed) {
      if (observed) ro.unobserve(observed);
      ro.observe(column);
      // The suggestions list changes the column's content height without
      // changing the column's own box, so watch the content too.
      if (column.firstElementChild) ro.observe(column.firstElementChild);
      column.querySelectorAll('.sss-cart-up, .cart-drawer__items').forEach((el) => ro.observe(el));
      observed = column;
    }
    recompute();
  };

  const start = () => {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return;
    new MutationObserver(attach).observe(drawer, { childList: true, subtree: true });
    drawer.addEventListener('toggle', attach, true);
    attach();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/*
 * Account popup: no "Sign in with Shop" button.
 *
 * The button and its "OR" divider live inside Shopify's <shopify-account>
 * web component, two shadow roots deep, where the theme's stylesheets can't
 * reach. Both roots are open, so a small style is dropped into each once it
 * exists: one hides the slotted <shop-login>, the other the divider. They
 * also round the close button and the email field, which don't follow the
 * component's radius tokens (set in sss-header.css). The
 * login form loads lazily when the popup first opens, hence the observer.
 * (Shop sign-in can also be switched off for the whole store in admin:
 * Settings > Customer accounts.)
 */
(() => {
  const addStyle = (root, css) => {
    if (!root || root.querySelector('style[data-sss]')) return;
    const style = document.createElement('style');
    style.dataset.sss = '';
    style.textContent = css;
    root.appendChild(style);
  };

  const patch = (account) => {
    const root = account.shadowRoot;
    if (!root) return false;
    // The close button becomes the same 30px glass disc as the cart's, the
    // search panel's and the menu's, with the same gold edge on focus.
    addStyle(
      root,
      'shop-login { display: none !important; }' +
        ' #close-button { width: 30px !important; height: 30px !important; min-width: 0 !important; padding: 0 !important;' +
        ' display: grid !important; place-items: center; border: 1px solid rgb(255 255 255 / 0.7) !important;' +
        ' border-radius: 50% !important; background: rgb(255 255 255 / 0.7) !important; color: #241e18 !important; box-shadow: none !important; }' +
        ' #close-button svg { width: 18px !important; height: 18px !important; }' +
        ' #close-button:focus-visible { outline: none !important; border-color: rgb(184 150 90 / 0.95) !important;' +
        ' box-shadow: inset 0 0 0 1px rgb(184 150 90 / 0.55) !important; }'
    );
    const form = root.querySelector('shopify-login-form');
    if (!form?.shadowRoot) return false;
    addStyle(
      form.shadowRoot,
      '.login-form__divider { display: none !important; }' +
        ' .login-form__email-input-wrapper, .login-form__email-input, .login-form__email-submit { border-radius: 999px !important; }'
    );
    return true;
  };

  const watch = (account) => {
    if (patch(account)) return;
    const mo = new MutationObserver(() => {
      if (patch(account)) mo.disconnect();
    });
    const target = account.shadowRoot || account;
    mo.observe(target, { childList: true, subtree: true });
    // The form's own shadow root is attached after it is inserted, which a
    // mutation observer doesn't see; check again whenever the popup opens.
    account.addEventListener('click', () => [50, 300, 1000].forEach((t) => setTimeout(() => patch(account), t)));
  };

  // Shopify pins the popup 20px from the right edge of the screen, which on a
  // wide monitor leaves it well away from the icon (the header stops at the
  // page width). Its right edge is lined up under the header's icons instead:
  // the distance from the screen edge to just past the cart icon is handed to
  // sss-header.css as --sss-account-right.
  const place = (account) => {
    const rect = account.getBoundingClientRect();
    if (!rect.width) return;
    const right = Math.max(10, Math.round(window.innerWidth - rect.right - 44));
    account.style.setProperty('--sss-account-right', `${right}px`);
  };

  const start = () => {
    const accounts = [...document.querySelectorAll('shopify-account')];
    accounts.forEach((account) => {
      watch(account);
      place(account);
      account.addEventListener('click', () => place(account), { capture: true });
    });
    window.addEventListener('resize', () => accounts.forEach(place), { passive: true });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/*
 * Region toggle (snippets/sss-region-toggle.liquid): tapping the
 * other country slides the glass thumb across first, then submits the
 * switch, so the movement is seen before the page changes.
 */
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const pill = event.target.closest('.sss-region__pill');
  if (!pill) return;
  event.preventDefault();
  if (pill.getAttribute('aria-pressed') === 'true') return;

  const toggle = pill.closest('.sss-region__toggle');
  const pills = [...toggle.querySelectorAll('.sss-region__pill')];
  pills.forEach((p) => p.setAttribute('aria-pressed', String(p === pill)));
  toggle.style.setProperty('--i', String(pills.indexOf(pill)));
  toggle.classList.add('is-moving');

  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  setTimeout(() => pill.form?.requestSubmit(pill), reduced ? 0 : 420);
});

/*
 * Floating header bar.
 *
 * Past a quarter of a screen of scrolling (on Home and About: once the hero
 * photograph has scrolled out of view), the header gets .sss-float and
 * sss-header.css turns it into the frosted gold bar pinned under the top
 * edge; back above that point it slides away and the header returns to its
 * place at the top of the page. Every page, the About page included.
 *
 * Before it floats, the header's wrapper is held at its current height (so
 * the page below doesn't jump when the header leaves the flow) and the
 * header measurements Dwell keeps on <body> are stored for the CSS to freeze.
 * Desktop scrolls inside .page-wrapper and phones scroll the document, hence
 * the capture listener on the document.
 */
(() => {
  const header = document.querySelector('#header-component');
  if (!header) return;

  const wrap = header.closest('.shopify-section') || header.parentElement;
  const root = document.documentElement;
  let floating = false;
  let raf = 0;
  let outTimer = 0;

  const scrollTop = () => {
    const page = document.querySelector('.page-wrapper');
    const inner = page && getComputedStyle(page).overflowY !== 'visible' ? page.scrollTop : 0;
    return Math.max(inner, window.scrollY || 0);
  };

  const rest = () => {
    header.classList.remove('sss-float', 'sss-float--out');
    document.body.classList.remove('sss-header-floating');
    wrap.style.minHeight = '';
  };

  const set = (next) => {
    if (next === floating) return;
    floating = next;
    clearTimeout(outTimer);
    if (next) {
      const body = getComputedStyle(document.body);
      root.style.setProperty('--sss-header-h', body.getPropertyValue('--header-height').trim() || '0px');
      root.style.setProperty('--sss-header-group-h', body.getPropertyValue('--header-group-height').trim() || '0px');
      wrap.style.minHeight = `${wrap.offsetHeight}px`;
      document.body.classList.add('sss-header-floating');
      header.classList.remove('sss-float--out');
      header.classList.add('sss-float');
    } else {
      header.classList.add('sss-float--out');
      outTimer = setTimeout(rest, 270);
    }
  };

  const update = () => {
    raf = 0;
    // Home and About open on a full photograph: there the bar waits until
    // the photograph has scrolled away completely.
    const hero = document.querySelector('.sss-hero, .sss-ab__hero');
    if (hero) set(hero.getBoundingClientRect().bottom <= 0);
    else set(scrollTop() > window.innerHeight * 0.25);
  };

  const queue = () => {
    if (!raf) raf = requestAnimationFrame(update);
  };

  document.addEventListener('scroll', queue, { capture: true, passive: true });
  window.addEventListener('resize', queue, { passive: true });
  update();
})();

/*
 * Policy page titles: Shopify prints a plain "Refund policy". The last word
 * goes in gold italic with a full stop ("Refund *policy.*"), matching the
 * heads of the Contact, FAQs and Size guide pages.
 */
(() => {
  const title = document.querySelector('.shopify-policy__title h1');
  if (!title || title.querySelector('em')) return;
  const words = title.textContent.trim().split(/\s+/);
  if (words.length < 2) return;
  const last = words.pop().replace(/\.$/, '');
  title.textContent = `${words.join(' ')} `;
  const em = document.createElement('em');
  em.textContent = `${last}.`;
  title.appendChild(em);
})();
